import { AnimatedDeck, AnimatedSlidesState, Asset, SidebarState, SidebarTab } from '.';
import { Broadcast } from '../event/types';
import { BroadcastActions, EventActions } from '../user/types';

import type { RemoteMediaStream } from '@/services/utils/liveMedia.utils';

import { AGORA } from '@/constants';
import i18n from '@/i18n';
import loggerService from '@/services/logger.service';
import { getVideoQNARequestsForBroadcastByStatus } from '@/services/videoQNA.service';
import { RootState } from '@/store';
import { resetSidbarSubTab } from '@/store/schedulers';
import { SIDEBAR_VISIBILITY_PERIOD } from '@/views/event/constants';

export const DEFAULT_ANIMATED_SLIDES_STATE: AnimatedSlidesState = {
  slideIndex: 0,
  stepNumber: 0,
  slidesCount: 0,
  currentSlideInput: [0, 0],
  slide_state_version: 0,
  maxStepCount: 0
};

const getters = {
  currentChat(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    let currentChat: any = null;
    if (state.selectedPrimaryTab === 'stage_chat') {
      const channel_id = getters.stageChatTabs[state.stageChatSubTabIndex]?.channel();
      currentChat = (rootState?.event?.avroom?.chats ?? []).find((it: any) => it.channel?.id === channel_id);
    } else if (state.selectedPrimaryTab === 'messages_chat') {
      currentChat = (rootState?.event?.avroom?.chats ?? []).find(
        (it: any) => it.channel?.id === state.currentDirectChatId
      );
    }

    return currentChat;
  },
  isOverNinetyDays(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const event = rootGetters['event/currentEvent'];
    const now = rootState.event.stage?.heartBeatDate || new Date();
    const endTime = new Date(event?.end_time);
    return endTime.getTime() < now.getTime() - SIDEBAR_VISIBILITY_PERIOD;
  },
  getAvailableTabs(
    state: SidebarState,
    getters: any,
    rootState: RootState,
    rootGetters: any
  ): Record<string, () => boolean> {
    const broadcast = rootGetters['event/currentBroadcast'];
    const onDemandMode = rootState.event.event?.on_demand_mode;
    const isTVMode = rootState.event?.stage?.isTVMode;
    const useHideSidebar = rootState.event.useHideSidebar;

    const isBroadcastSpeakerOrOrganizer = rootGetters['event/isBroadcastSpeakerOrOrganizer'];

    if (getters.isOverNinetyDays && useHideSidebar) {
      return {
        docs: () => broadcast?.id && rootGetters['event/currentPageResources']?.length > 0,
        kudo: () => broadcast?.kudo_config?.enabled ?? false,
        leaderboard: () => !!rootState.event.event?.validar_leaderboard_url,
        vcapture: () => !!broadcast?.validar_vcapture_url,
        survey_iframe: () => {
          const isCventEvent = !!rootState.event.event?.cvent_event_id;
          const surveyEnabled = rootState.event.surveyIframe?.enabled;
          const hasUrl = !!rootState.event.surveyIframe?.url;
          return isCventEvent && surveyEnabled && hasUrl;
        }
      };
    }

    return {
      stage_chat: () => getters.stageChatTabs.length > 0,
      messages: () => !rootState.event.event?.p2p_chat_disabled,
      poll: () => {
        if (broadcast?.slido_poll_embed_url) {
          return true;
        }

        if (broadcast?.poll_channel_mmid !== rootState.event?.avroom?.polls?.id) {
          return false;
        }

        if ((onDemandMode && !rootState.event?.useOnDemandMQL) || !broadcast?.id || isTVMode) {
          return false;
        }

        if (!Object.keys(rootState.event?.avroom?.polls?.details?.questions || {}).length) {
          return false;
        }

        if (onDemandMode && rootState.event?.useOnDemandMQL) {
          return Object.values(rootState.event?.avroom?.polls?.details?.questions || {}).some(
            (poll: any) => poll?.show_in_on_demand === true
          );
        }

        if (isBroadcastSpeakerOrOrganizer && !rootGetters['event/stage/showNewStageControls']) {
          return true;
        }

        return !!rootGetters['event/avroom/openedPollData'];
      },
      docs: () => broadcast?.id && rootGetters['event/currentPageResources']?.length > 0,
      qa: () => broadcast?.id && broadcast?.have_text_qna,
      assets: () =>
        !rootGetters['event/stage/showNewStageControls'] &&
        getters.assetsTabSubTabs.length > 0 &&
        rootGetters['event/isBroadcastSpeakerOrOrganizer'] &&
        !onDemandMode,
      survey: () =>
        broadcast?.id &&
        broadcast?.surveys?.length !== 0 &&
        !onDemandMode &&
        !rootState.event?.stage?.isTVMode &&
        rootGetters['event/isBroadcastSpeakerOrOrganizer'] &&
        !rootGetters['event/stage/showNewStageControls'],

      messages_chat: () => !rootState.event.event?.p2p_chat_disabled,
      kudo: () => broadcast?.kudo_config?.enabled ?? false,
      leaderboard: () => !!rootState.event.event?.validar_leaderboard_url,
      vcapture: () => !!broadcast?.validar_vcapture_url,
      survey_iframe: () => {
        const isCventEvent = !!rootState.event.event?.cvent_event_id;
        if (!isCventEvent) {
          return false;
        }

        const surveyEnabled = rootState.event.surveyIframe?.enabled;
        if (!surveyEnabled) {
          return false;
        }

        const hasUrl = !!rootState.event.surveyIframe?.url;
        if (!hasUrl) {
          return false;
        }

        return !isTVMode;
      }
    };
  },
  availableTabs(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast = rootGetters['event/currentBroadcast'];

    const availability = getters.getAvailableTabs;

    const customNames: Record<string, string> = {
      kudo: broadcast?.kudo_config?.title ?? i18n.global.t('@@translate@@ Kudo')
    };

    const tabs = [
      {
        name: i18n.global.t('@@translate@@ Chat'),
        id: 'stage_chat',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Messages'),
        id: 'messages',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Poll'),
        id: 'poll',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Docs'),
        id: 'docs',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Q&A'),
        id: 'qa',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Media'),
        id: 'assets',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Survey'),
        id: 'survey',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Messages Chat'),
        id: 'messages_chat',
        hidden: true,
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Kudo'),
        id: 'kudo',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Leaderboard'),
        id: 'leaderboard',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ VCapture'),
        id: 'vcapture',
        available: () => false
      },
      {
        name: i18n.global.t('@@translate@@ Survey'),
        id: 'survey_iframe',
        available: () => false
      }
    ].map(it => {
      let modifiedTab = it;
      if (availability[it.id]) {
        modifiedTab = {
          ...it,
          available: availability[it.id]
        };
      }

      if (customNames[it.id]) {
        modifiedTab = {
          ...modifiedTab,
          name: customNames[it.id]
        };
      }

      return modifiedTab;
    });

    return tabs.filter(tab => tab.available());
  },
  sidebartabs(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast: Broadcast = rootGetters['event/currentBroadcast'];
    const stage_chat_id = rootState.mattermost?.publicChannels?.offtopic;

    return getters.availableTabs.reduce((acc: SidebarTab[], it: SidebarTab) => {
      if (it.hidden) {
        return acc;
      }

      if (it.id === 'messages') {
        const dms: Array<{ id: string; user: any; total_unread: number }> = rootGetters['event/avroom/latestChats'];
        const currentChat = getters.currentChat;

        it.indicatorDetails = dms
          .map(dm => {
            return {
              id: dm.id,
              user: dm.user,
              count: dm.total_unread
            };
          })
          .filter(indicator_detail => indicator_detail.id !== currentChat?.channel?.id && indicator_detail.count > 0);

        it.showIndicator = state.selectedPrimaryTab !== 'messages' && it.indicatorDetails.length > 0;
      } else if (it.id === 'poll') {
        it.showIndicator = !!rootGetters['event/avroom/openedPollData'];
        if (broadcast) {
          it.loadingstate = rootState.event.avroom?.channel_loading[broadcast.poll_channel_mmid];
        }
      } else if (it.id === 'qa') {
        const questions = rootGetters['event/textQNA/textQNADetails']?.questions || [];
        const hasUnansweredQuestions = questions.filter((it: any) => !it.answered).length > 0;

        const showIndicatorForVideoQnA =
          getVideoQNARequestsForBroadcastByStatus(broadcast, 'OPEN').length > 0 &&
          rootGetters['event/isBroadcastSpeakerOrOrganizer'];

        it.showIndicator =
          (hasUnansweredQuestions || showIndicatorForVideoQnA) && !rootGetters['event/stage/showNewStageControls'];

        if (broadcast) {
          const qna_loading_state = rootState.event.avroom?.channel_loading[broadcast.text_qna_channel_mmid];

          it.loadingstate = qna_loading_state === 'loading' ? 'loading' : 'loaded';
        }
      } else if (it.id === 'stage_chat' && stage_chat_id) {
        const channel_loading = rootState.event?.avroom?.channel_loading[stage_chat_id];
        it.loadingstate = channel_loading === 'loading' ? 'loading' : 'loaded';

        const currentBroadcast = rootGetters['event/currentBroadcast'];

        const stageChatChannel = rootState.event.avroom?.chats.find(it => it.channel.id === stage_chat_id);
        const speakerChatChannel = rootState.event.avroom?.chats.find(
          it => it.channel.id === currentBroadcast?.speaker_chat_mmid
        );
        const sessionChatChannel = rootState.event.avroom?.chats.find(
          it => it.channel.id === getters.sessionChatChannel
        );

        const showSessionTab =
          (currentBroadcast && currentBroadcast?.id && !rootState.event.event?.broadcast_chat_disabled) ||
          (rootState.route.name === 'DiscussionRoom' && !rootState.event.event?.discussions_chat_disabled);

        const showRoomTab = rootGetters['event/currentEventBooth'] && !rootState.event.event?.booth_chat_disabled;

        // unread message count for booths
        let boothChatChannel;
        if (rootGetters['event/currentEventBooth']) {
          boothChatChannel = rootState.event.avroom?.chats.find(
            it => it.channel.id === rootGetters['event/currentEventBooth'].mmid
          );
        }

        const totalUnread =
          (stageChatChannel?.channel?.total_unread || 0) +
          ((!rootGetters['event/stage/showNewStageControls'] &&
            rootGetters['event/isBroadcastSpeakerOrOrganizer'] &&
            speakerChatChannel?.channel?.total_unread) ||
            0) +
          ((showSessionTab && sessionChatChannel?.channel?.total_unread) || 0) +
          ((showRoomTab && boothChatChannel?.channel?.total_unread) || 0);

        it.indicatorDetails = [
          {
            user: null,
            count: totalUnread
          }
        ];

        it.showIndicator = state.selectedPrimaryTab !== 'stage_chat' && totalUnread > 0;
      }

      acc.push(it);
      return acc;
    }, []);
  },
  stageChatTabs(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const tabs: any = [];
    const onDemandMode = rootState.event.event?.on_demand_mode;
    const currentBroadcast = rootGetters['event/currentBroadcast'];
    if (
      currentBroadcast &&
      currentBroadcast?.id &&
      rootGetters['event/isBroadcastSpeakerOrOrganizer'] &&
      !rootState.event.stage?.isTVMode &&
      !rootGetters['event/stage/showNewStageControls'] &&
      !onDemandMode
    ) {
      let description = '';
      if (state.showSpeakerChatDescription) {
        description = i18n.global.t("@@translate@@ Speakers only, attendees can't see this chat");
      }
      const speakerChatChannel = rootState.event.avroom?.chats.find(
        it => it.channel.id === currentBroadcast?.speaker_chat_mmid
      );
      tabs.push({
        id: 'speaker_chat',
        label: i18n.global.t('@@translate@@ Speakers'),
        channel: () => currentBroadcast?.speaker_chat_mmid,
        icon: '',
        description,
        count: speakerChatChannel?.channel.total_unread || 0
      });
    }

    // Check if room chat tab is to be shown
    const showSessionTab =
      (currentBroadcast && currentBroadcast?.id && !rootState.event.event?.broadcast_chat_disabled) ||
      (rootState.route.name === 'DiscussionRoom' && !rootState.event.event?.discussions_chat_disabled);

    const sessionChatChannel = rootState.event.avroom?.chats.find(it => it.channel.id === getters.sessionChatChannel);
    const sessionChatUnreadCount = sessionChatChannel?.channel?.total_unread || 0;
    if (showSessionTab) {
      tabs.push({
        id: 'session',
        label:
          rootState.route.name === 'DiscussionRoom'
            ? i18n.global.t('@@translate@@ Room')
            : i18n.global.t('@@translate@@ Session'),
        channel: () => getters.sessionChatChannel,
        icon: '',
        count: sessionChatUnreadCount
      });
    }

    if (rootGetters['event/currentEventBooth'] && !rootState.event.event.booth_chat_disabled) {
      const boothChatChannel = rootState.event.avroom?.chats.find(
        it => it.channel.id === rootGetters['event/currentEventBooth'].mmid
      );
      tabs.push({
        id: 'booth',
        label: i18n.global.t('@@translate@@ Booth'),
        channel: () => rootGetters['event/currentEventBooth'].mmid,
        icon: '',
        count: boothChatChannel?.channel?.total_unread || 0
      });
    }

    const public_chat_channel = rootState.event.avroom?.chats.find(
      it => it.channel.id === rootState.mattermost.publicChannels.offtopic
    );
    const unreadInPublic = public_chat_channel?.channel?.total_unread || 0;
    const publicChatHidden = rootState.event.event.chat_visibilty === 'OFF';
    if (!publicChatHidden) {
      tabs.push({
        id: 'public',
        label: i18n.global.t('@@translate@@ Public'),
        channel: () => rootState.mattermost?.publicChannels?.offtopic,
        icon: rootState.event.event.chat_visibilty === 'PRIVATE' ? 'lock' : '',
        count: unreadInPublic
      });
    }

    if (rootState.event.event?.drift_bot_enabled) {
      tabs.push({
        label: rootState.event.event.drift_tab_name || i18n.global.t('@@translate@@ Drift'),
        id: 'drift_embed',
        channel: () => 'drift',
        icon: ''
      });
    }

    let selectedSubtabIndex = state.stageChatSubTabIndex;
    if (!tabs[selectedSubtabIndex]) {
      loggerService.warn(`Selected tab index is ${selectedSubtabIndex} but only ${tabs.length} tabs were found!`);
      selectedSubtabIndex = 0;
      resetSidbarSubTab();
    }
    return tabs.map((tab: any, index) => ({
      ...tab,
      selected: index === selectedSubtabIndex
    }));
  },
  activeStageChatTab(state: SidebarState, getters: any) {
    return getters.stageChatTabs.find(({ selected }: any) => selected);
  },
  activeStageChatTabIsPublic(state: SidebarState, getters: any) {
    return getters.activeStageChatTab?.id === 'public';
  },
  sessionChatChannel(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    if (rootState.route.name === 'DiscussionRoom') {
      return rootGetters['event/getDiscussionGroup'](rootState.route.params.id)?.mmid;
    }
    return rootGetters['event/currentBroadcast']?.mmid;
  },
  currentPrimaryTabId(state: SidebarState, getters: any) {
    if ((getters.availableTabs as SidebarTab[]).some(tab => tab.id === state.selectedPrimaryTab)) {
      return state.selectedPrimaryTab;
    }
    return getters.availableTabs[0]?.id;
  },
  assetsTabSubTabs(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const tabs: any = [];
    const broadcast = rootGetters['event/currentBroadcast'];

    const broadcastType = broadcast?.broadcast_type;

    if (broadcastType === 'LIVE_HYBRID' && broadcast?.slide_assets?.length > 0) {
      tabs.push({
        id: 'slides',
        label: i18n.global.t('@@translate@@ Slides')
      });
    }

    if (broadcastType === 'LIVE_HYBRID' && broadcast?.video_assets?.length > 0) {
      tabs.push({
        id: 'vids',
        label: i18n.global.t('@@translate@@ Videos')
      });
    }

    if (broadcast?.id && rootGetters['isEventOrganizer']) {
      tabs.push({
        id: 'banner',
        label: i18n.global.t('@@translate@@ Banners')
      });
    }

    if (broadcast?.id && (getters.stageLogos.length > 0 || broadcast.stage_logo_spec)) {
      tabs.push({
        id: 'logos',
        label: i18n.global.t('@@translate@@ Images')
      });
    }

    return tabs;
  },
  areLocalSlidesPublished(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    return rootGetters['event/isLocalSlidesRecordingEnabled'] && state?.rsSlidesCanvas?.canvasElement;
  },
  assetsCurrentTabId(state: SidebarState, getters: any) {
    return getters.assetsTabSubTabs[state.assetsSubTabIndex]?.id || getters.assetsTabSubTabs[0]?.id;
  },
  slideTabProps(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast = rootGetters['event/currentBroadcast'];
    const assets = broadcast?.slide_assets || [];

    const decks = assets.map((slide: Asset) => state.slideDecks.slides[slide.id]).filter((slide: Asset) => !!slide);

    return {
      currentDeck: getDeck(broadcast?.current_playing_slide_asset),
      onsidebar: getDeck(state.slideDecks.onsidebar),
      decks: decks,
      // One based indexing because pdfjs has it
      currentPage: broadcast?.current_playing_slide_number + 1
    };

    function getDeck(id: string) {
      const deck = id && assets.find((asset: any) => asset.id === id);
      if (!deck) {
        return null;
      }
      return {
        ...deck,
        ...state.slideDecks.slides[id],
        // One based indexing because pdfjs has it
        currentPage: broadcast?.current_playing_slide_number + 1,
        isPublished: broadcast?.current_playing_slide_asset === id
      };
    }
  },

  animatedSlideTabProps(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast = rootGetters['event/currentBroadcast'];
    const assets: Asset[] = broadcast?.slide_assets || [];
    const animatedAssets = assets.filter((a: Asset) => a.is_animations_enabled);

    const decks = animatedAssets
      .map((asset: Asset) => state.animatedSlideDecks.decks[asset.id])
      .filter((deck: AnimatedDeck | undefined) => !!deck);

    return {
      currentDeck: getAnimatedDeck(broadcast?.current_playing_slide_asset),
      onSidebar: getAnimatedDeck(state.animatedSlideDecks.onsidebar),
      decks,
      currentSlideNumber: broadcast?.current_playing_slide_number || 1,
      currentStepNumber: broadcast?.current_playing_step_number || 1
    };

    function getAnimatedDeck(id: string): (AnimatedDeck & { isPublished: boolean }) | null {
      if (!id) {
        return null;
      }

      const asset = animatedAssets.find((a: Asset) => a.id === id);
      if (!asset) {
        return null;
      }

      const deck = state.animatedSlideDecks.decks[id];
      if (!deck) {
        return null;
      }

      return {
        ...deck,
        isPublished: broadcast?.current_playing_slide_asset === id
      };
    }
  },

  publishedStage(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    let currentScreen,
      loading = false,
      showControls = rootGetters['event/isBroadcastSpeakerOrOrganizer'],
      isHls = false,
      params = {},
      isOverridable = true,
      visibleOnStage = true,
      title = '';

    const currentBroadcast = rootGetters['event/currentBroadcast'];
    const broadcast = rootGetters['event/currentBroadcast'];

    function isShowingHls() {
      return (
        broadcast &&
        (broadcast.broadcast_type === 'SIMULIVE' ||
          broadcast.broadcast_type === 'LIVE_EXTERNAL' ||
          (broadcast.broadcast_type === 'LIVE_HYBRID' &&
            broadcast.current_playing_video_asset &&
            broadcast.playback_start_time))
      );
    }

    const roomId = rootState.route.params.id?.toString();
    const remoteStreamsKeys = roomId ? Object.keys(rootState.mediaroom.mediaRooms[roomId]?.remoteStreams || {}) : [];
    const remoteScreenStreamUid = remoteStreamsKeys?.find(
      (uid: string) => uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM) && !uid.includes(AGORA.UID_SUFFIX.RECORDER)
    );
    const hasRemoteScreenStream = !!remoteScreenStreamUid;
    const hasLocalScreenTrack = !!rootState.mediaroom.screenTracks.length;
    const currentUserId = rootState.user.currentUser?.id;
    let isCurrentUserScreenSharer = false;
    let isCurrentScreenShareSlides = false;
    if (currentUserId) {
      isCurrentUserScreenSharer = remoteStreamsKeys?.some(
        (uid: string) => uid.includes(currentUserId) && uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)
      );
    }

    // TODO
    if (hasRemoteScreenStream) {
      isCurrentScreenShareSlides = remoteScreenStreamUid.includes('slide');
    }
    const hasCustomTracks = rootState.mediaroom.screenTracks?.some(elem => elem.trackId?.includes('cus'));
    const areRSLocalSlidesPublished =
      (rootGetters['event/isLocalSlidesRecordingEnabled'] || hasCustomTracks) &&
      (state?.rsSlidesCanvas?.canvasElement || getters?.slideTabProps.currentDeck?.isPublished);
    const onDemandMode = rootState.event.event?.on_demand_mode && currentBroadcast?.on_demand_asset;

    if (onDemandMode) {
      currentScreen = null;
      showControls = null;
      isOverridable = false;
    } else if (isShowingHls() && broadcast.broadcast_type === 'LIVE_HYBRID') {
      const currentAsset = broadcast?.video_assets.find(i => i.id === broadcast?.current_playing_video_asset);
      currentScreen = 'live_hls';
      isOverridable = false;
      showControls = rootGetters['event/stage/showNewStageControls'] || false;
      title = currentAsset?.name || 'Video';
      visibleOnStage = false;
      isHls = true;
      params = currentAsset;
      // For RS slides, we have slides shared and local screen track - that's why !areRSSlidesPublished
    } else if (!areRSLocalSlidesPublished && (hasLocalScreenTrack || hasRemoteScreenStream)) {
      if (isCurrentScreenShareSlides) {
        currentScreen = 'slide_animations';
        title = getters?.slideTabProps.currentDeck?.name || 'Slides';
        loading = getters?.slideTabProps.currentDeck?.loading;
        params = getters.slideTabProps.currentDeck;
        showControls = rootGetters['event/isBroadcastSpeakerOrOrganizer'];
      } else {
        title = 'Shared Screen';
        currentScreen = 'screen';
        showControls = hasLocalScreenTrack;
      }
      isOverridable = false;
      loading = rootState.mediaroom.screenTracks?.some(elem => elem.trackStatus !== 'PUBLISHED');
      const streamId = remoteScreenStreamUid || '';
      const stream: RemoteMediaStream = {
        id: streamId,
        ...rootState.mediaroom.mediaRooms[roomId]?.remoteStreams[streamId],
        loading
      };

      if (!isCurrentUserScreenSharer) {
        stream.signalStrength = rootState.mediaroom.remoteSignalMetadata?.[streamId]?.strength;
      }

      params = {
        ...params,
        stream,
        hideOwnScreen: false,
        screenPublishing: true,
        screenLoaded: true,
        muted: rootState.event?.stage?.muted,
        audioOutputDeviceId: rootState.mediaroom.currentDevices?.speaker?.deviceId,
        isLocalScreenStream: stream?.id?.includes(rootGetters.getCurrentUser?.id)
      };
    } else if (rootState.route.name === 'DiscussionRoom') {
      currentScreen = null;
    } else {
      if (rootGetters['event/textQNA/publishedTextQNA']) {
        currentScreen = 'textQna';
        title = 'Text Q&A';
        params = rootGetters['event/textQNA/publishedTextQNA'];
      } else if (currentBroadcast?.have_polls && rootGetters['event/avroom/publishedPollData']?.id) {
        currentScreen = 'poll';
        title = 'Poll';
        params = rootGetters['event/avroom/publishedPollData'] || {};
      } else if (getters?.slideTabProps.currentDeck?.isPublished) {
        const regularDeck = getters?.slideTabProps.currentDeck;
        const animatedDeck = getters?.animatedSlideTabProps?.currentDeck;
        const isAnimatedSlides = animatedDeck?.is_animations_enabled && rootState.event.useNewSlideAnimation;

        if (isAnimatedSlides && animatedDeck) {
          const cdnUrl = animatedDeck.cdnBaseUrl;
          const presUrl = animatedDeck.path ? `${cdnUrl}${animatedDeck.path}` : '';
          currentScreen = 'new_slide_animations';
          title = animatedDeck.name || 'Animated Slides';
          loading = false;
          params = {
            ...animatedDeck,
            presUrl,
            currentSlideNumber: getters?.animatedSlideTabProps?.currentSlideNumber || 1
          };
        } else {
          currentScreen = 'slide';
          title = regularDeck?.name || 'Slides';
          loading = regularDeck?.loading;
          params = regularDeck;
        }
      } else if (currentBroadcast?.show_overlay) {
        title = 'Broadcast Overlay';
        currentScreen = 'broadcastOverlay';
        isOverridable = false;
        showControls = rootGetters['isEventOrganizer'];
        params = rootGetters['event/stage/currentBroadcastOverlay'];
      }
    }

    return {
      title,
      currentScreen,
      loading,
      isOverridable,
      showControls: !!currentScreen && showControls,
      params,
      isHls,
      visibleOnStage: !!currentScreen && visibleOnStage
    };
  },
  stageLogos(state: SidebarState, getters: any, rootState: RootState) {
    const { theme_obj = {} } = rootState.event.event || {};
    return theme_obj.logos || [];
  },
  currentSlideUrl(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast = rootGetters['event/currentBroadcast'];
    return state.slideDecks?.slides[broadcast?.current_playing_slide_asset]?.slides[
      broadcast?.current_playing_slide_number
    ]?.high;
  },
  isPublishingRSSlides(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    return state?.isPublishingRSSlides;
  },
  isChatActionPermitted:
    (state: SidebarState, getters: any, rootState: RootState, rootGetters: any) =>
    (action: BroadcastActions): boolean => {
      const isBroadcastActionPermitted = rootGetters['isBroadcastActionPermitted'];
      const isEventActionPermitted = rootGetters['isEventActionPermitted'];
      const currentBroadcast = rootGetters['event/currentBroadcast'];

      const shouldUseEventAction = currentBroadcast && Object.keys(currentBroadcast).length === 0;
      const isPermitted = shouldUseEventAction
        ? isEventActionPermitted(EventActions.ControlBoothAndRoomChat)
        : isBroadcastActionPermitted(BroadcastActions.CONTROL_CHAT, currentBroadcast.id);

      if (isPermitted) {
        return true;
      }

      return isBroadcastActionPermitted(action, currentBroadcast.id);
    },

  isAnimatedSlidesEnabled(state: SidebarState, getters: any, rootState: RootState, rootGetters: any) {
    const currentDeck = getters?.slideTabProps?.currentDeck;
    return currentDeck?.is_animations_enabled && rootState.event.useNewSlideAnimation;
  },

  animatedSlides(state: SidebarState): AnimatedSlidesState {
    return state.animatedSlides ?? DEFAULT_ANIMATED_SLIDES_STATE;
  }
};

export default getters;
export type SidbarStateGetters = typeof getters;
